// src/components/unit/Unit.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Unit.css";

import {
    fetchMathApiWithAuth,
    fetchMathApiNoAuth,
} from "../../utils/mathServerHttp";

const Unit = () => {
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userInfo, setUserInfo] = useState({});

    // 控制“头像菜单”显示/隐藏
    const [showMenu, setShowMenu] = useState(false);

    // 控制“切换年级”下拉列表的显示/隐藏
    const [showGradeOptions, setShowGradeOptions] = useState(false);

    // 年级列表数据
    const [gradeList, setGradeList] = useState([]);

    // 选中的年级 code（可用于下拉框）
    const [selectedGrade, setSelectedGrade] = useState(null);

    const menuRef = useRef(null);
    const navigate = useNavigate();

    // ---------------------------
    // 初始化：获取用户信息和默认年级的单元
    // ---------------------------
    useEffect(() => {
        const fetchUnits = async () => {
            setLoading(true);
            try {
                // 1. 获取用户信息
                const userData = await fetchMathApiWithAuth(`/api/user/info`);
                setUserInfo(userData);

                // 保存用户当前年级到状态，用于后续标记下拉选中
                if (userData.grade) {
                    setSelectedGrade(userData.grade);
                }

                const grade = userData.grade ? userData.grade : 141;
                // 2. 获取单元信息
                const unitData = await fetchMathApiWithAuth(
                    `/api/unit/all?grade=${grade}`
                );
                setUnits(unitData);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchUnits();
    }, []);

    // ---------------------------
    // 点击章节后，跳转到 /paper/:chapterId
    // ---------------------------
    const handleNavigate = (unitId, chapterId, chapterLevel) => {
        navigate(`/paper?chapterId=${chapterId}&chapterLevel=${chapterLevel}`);
    };

    // ---------------------------
    // 点击错题展现按钮，跳转到错题复习页面
    // ---------------------------
    const handleWrongReview = (unitId, chapterId) => {
        const params = new URLSearchParams({ unitId });

        if (chapterId) {
            params.append("chapterId", chapterId);
        }

        navigate(`/wrongReview?${params.toString()}`);
    };


    // ---------------------------
    // 点击头像，切换菜单显示状态
    // ---------------------------
    const handleAvatarClick = () => {
        setShowMenu(!showMenu);
        setShowGradeOptions(false); // 关闭“切换年级”子菜单
    };

    // 如果点击区域在菜单外，则关闭菜单
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setShowMenu(false);
                setShowGradeOptions(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // ---------------------------
    // 退出登录
    // ---------------------------
    const handleLogout = async () => {
        try {
            await fetchMathApiWithAuth("/api/user/logout", {
                method: "POST",
            });
        } catch (err) {
            console.log(err.message);
            setError(err.message || "退出登录失败");
        } finally {
            navigate("/");
        }
    };

    // ---------------------------
    // 切换年级 - 第一步：获取年级列表
    // ---------------------------
    const handleShowGradeOptions = async () => {
        try {
            const data = await fetchMathApiNoAuth("/api/grade/list");
            setGradeList(data || []);

            // 将“用户当前年级”设为选中状态
            if (userInfo.grade) {
                setSelectedGrade(userInfo.grade);
            }

            setShowGradeOptions(true); // 显示下拉列表
        } catch (err) {
            setError(err.message || "获取年级列表失败");
        }
    };

    // ---------------------------
    // 切换年级 - 第二步：选择某个年级
    // ---------------------------
    const handleSelectGrade = async (code) => {
        setSelectedGrade(code);

        // 调用 /api/grade/change 接口
        try {
            await fetchMathApiWithAuth("/api/grade/change", {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: new URLSearchParams({ grade: String(code) }),
            });

            // 再次刷新用户信息，确保 userInfo.grade 也更新了
            const newUserData = await fetchMathApiWithAuth("/api/user/info");
            setUserInfo(newUserData);

            // 刷新单元数据
            const unitData = await fetchMathApiWithAuth(
                `/api/unit/all?grade=${code}`
            );
            setUnits(unitData);

            // 关闭弹窗
            setShowMenu(false);
            setShowGradeOptions(false);
        } catch (err) {
            setError(err.message || "切换年级失败");
        }
    };

    // ---------------------------
    // 处理升级点击事件
    // ---------------------------
    const handleUpgradeLevel = async (unitIndex, chapterIndex, chapter) => {
        try {
            const response = await fetchMathApiWithAuth("/api/unit/chapter/upgradeLevel", {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: new URLSearchParams({
                    chapterId: chapter.chapterId,
                    chapterLevel: chapter.upgradeLevel
                })
            });

            // 假设请求成功，只刷新对应章节的星星和月亮显示
            const newUnits = [...units];
            const unit = newUnits[unitIndex];
            const newChapterList = [...unit.chapterList];
            const newChapter = { ...newChapterList[chapterIndex] };
            newChapter.chapterLevel = chapter.upgradeLevel;
            newChapter.canUpgrade = false;
            newChapterList[chapterIndex] = newChapter;
            unit.chapterList = newChapterList;
            newUnits[unitIndex] = unit;

            setUnits(newUnits);
        } catch (err) {
            console.error("升级失败:", err);
        }
    };

    if (loading) {
        return <div className="loading">加载中...</div>;
    }

    if (error) {
        return <div className="error">错误: {error}</div>;
    }

    return (
        <div className="App">
            {/* 头部标题 + 头像 */}
            <div className="app-header">
                <h1 className="app-title">
                    饭仔多多国
                    {userInfo.stars > 0 && `⭐️${userInfo.stars}`}
                    {userInfo.moons > 0 && `🌙️${userInfo.moons}`}
                </h1>
                {/* 头像区域 */}
                <div className="avatar-container" ref={menuRef}>
                    <img
                        src={userInfo.avatar ? userInfo.avatar : "/logo192.png"}
                        alt="Avatar"
                        className="avatar"
                        onClick={handleAvatarClick}
                    />

                    {/* 菜单，下拉弹窗 */}
                    {showMenu && (
                        <div className="dropdown-menu">
                            <div
                                className="dropdown-item"
                                onClick={handleShowGradeOptions}
                            >
                                切换年级
                            </div>
                            <div className="dropdown-item" onClick={handleLogout}>
                                退出登录
                            </div>

                            {/* 如果 showGradeOptions=true，则再显示一个选择区 */}
                            {showGradeOptions && (
                                <div className="grade-options">
                                    {gradeList.map((g) => (
                                        <div
                                            key={g.code}
                                            // 如果 g.code === selectedGrade，就加一个选中样式
                                            className={`grade-option ${
                                                g.code === selectedGrade ? "selected" : ""
                                            }`}
                                            onClick={() => handleSelectGrade(g.code)}
                                        >
                                            {g.name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* 单元内容 */}
            <div className="unit-container">
                {units.map((unit, unitIndex) => (
                    <div className="tree" key={unit.unitId}>
                        <h2 className="unit-title">
                            {unit.name}
                            {unit.hasWrong && (
                                <span
                                    className="wrong-review-btn"
                                    onClick={() => handleWrongReview(unit.unitId, null)}
                                >
                                    💫️
                                </span>
                            )}
                        </h2>
                        <ul>
                            {unit.chapterList.map((chapter, chapterIndex) => (
                                <li key={chapter.chapterId} className="chapter-item">
                                    <span className={`award-btn ${chapter.chapterLevel === 1 ? 'star' : chapter.chapterLevel === 2 ? 'moon' : ''}`}></span>
                                    <span className="node-text"
                                          onClick={() =>
                                              handleNavigate(unit.unitId, chapter.chapterId, chapter.chapterLevel)
                                          }>
                                        {chapter.name}
                                        &nbsp;
                                        {"⭐️".repeat(chapter.chapterLevel === 1 && chapter.stars || 0)}
                                        {"🌙".repeat(chapter.chapterLevel === 2 && chapter.moons || 0)}
                                    </span>
                                    {chapter.canUpgrade === true && (
                                        <span
                                            className="award-btn upgrade-level"
                                            onClick={() => handleUpgradeLevel(unitIndex, chapterIndex, chapter)}
                                        >
                                        </span>
                                    )}
                                    {chapter.hasWrong && (
                                        <span
                                            className="wrong-review-btn"
                                            onClick={() => handleWrongReview(unit.unitId, chapter.chapterId)}
                                        >
                                            💫️
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Unit;